import {
    useState,
    useCallback,
    useEffect,
    useRef,
} from 'react';
import {
    webLibSdk,
    getCurrentTimeZoneDate,
} from 'helpers';
import Button from 'components/UI/Button';
import toastAlert from 'components/UI/Alert';
import {
    TRANSACTION_PENDING_MODAL,
    SEND_ALLOCATION_ADDRESS_MODAL,
} from '_constants/modalTypes';
import { useModal } from 'hooks';

const CancelAllocation = ({
    amountClaimed, allocationTime, address, state, hasAccess, isCustom, cancelation,
}) => {
    const [loading, setLoading] = useState(false);
    const [disabled, setDisabled] = useState(true);
    const { onOpen: onOpenModal } = useModal();
    const timerRef = useRef(null);
    const day = 60 * 60 * 24;

    const cancelAllocationFinally = useCallback(async (event, ...attr) => {
        try {
            const result = await webLibSdk[event](address, ...attr);

            onOpenModal(TRANSACTION_PENDING_MODAL, { txHash: result.txHash, refetch: () => window.location.reload() });
        } catch (err) {
            toastAlert({
                type: 'error',
                message: "You can't cancel this allocation",
                customMessage: err,
            });
        } finally {
            setLoading(false);
        }
    }, [address, onOpenModal]);

    const cancelAllocationHandler = useCallback(async () => {
        setLoading(true);
        if (isCustom) {
            onOpenModal(SEND_ALLOCATION_ADDRESS_MODAL, { setLoading, sendAllocation: cancelAllocationFinally, event: 'customCancelAllocation' });
        } else {
            cancelAllocationFinally('cancelAllocation');
        }
    }, [cancelAllocationFinally, isCustom, onOpenModal]);

    const hasCancelAccess = useCallback(() => {
        const isNotDisabled = !Number(amountClaimed) && hasAccess && ((isCustom && cancelation) || (new Date() - new Date(getCurrentTimeZoneDate(allocationTime))) / 1000 < day) && state !== 'Canceled';
        if (!isNotDisabled) clearInterval(timerRef.current);
        setDisabled(!isNotDisabled);
    }, [amountClaimed, hasAccess, isCustom, cancelation, allocationTime, day, state]);

    const hasCancelAccessTimer = useCallback(() => {
        clearInterval(timerRef.current);
        timerRef.current = setInterval(hasCancelAccess, 1000);
    }, [hasCancelAccess]);

    useEffect(() => {
        hasCancelAccess();
        hasCancelAccessTimer();

        return () => {
            clearInterval(timerRef.current);
        };
    }, [hasCancelAccessTimer]);

    return (
        <Button
            className="cancel-btn"
            disabled={disabled}
            onClick={() => cancelAllocationHandler(address)}
            loading={loading}
        >
            Cancel
        </Button>
    );
};

export default CancelAllocation;
